import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { isInAppBrowser } from '../utils/browser';

export default function LoginSheet({ onCancel, onGuestCheckout }) {
  const { login } = useAuth();
  const [inApp] = useState(isInAppBrowser);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — guest can still use the ⋯ menu */ }
  };

  return (
    <div style={s.overlay} onClick={onCancel}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        style={s.sheet} onClick={(e) => e.stopPropagation()}
      >
        <div style={s.handle} />
        <h2 style={s.title}>How would you like to order?</h2>

        {inApp && (
          <div style={s.warn}>
            <span style={s.warnIcon}>⚠️</span>
            <div>
              <p style={s.warnTitle}>Open in your browser first</p>
              <p style={s.warnText}>
                Sign-in won&apos;t finish inside this app&apos;s browser. Tap the ⋯ menu and choose
                “Open in Safari/Chrome”, or copy the link and paste it there.
              </p>
              <button style={s.warnBtn} onClick={copyLink}>{copied ? 'Link copied ✓' : 'Copy link'}</button>
            </div>
          </div>
        )}

        {/* Apple first: most GapGo guests scan a QR on an iPhone, and Apple's
            own guidance puts its button above other providers when offered. */}
        <button style={s.appleBtn} onClick={() => login('Apple')}>
          <AppleIcon />
          <span>Continue with Apple</span>
        </button>

        <button style={s.googleBtn} onClick={() => login('Google')}>
          <GoogleIcon />
          <span>Continue with Google</span>
        </button>

        {onGuestCheckout && (
          <button style={s.guestBtn} onClick={onGuestCheckout}>
            Order as Guest
          </button>
        )}
      </motion.div>
    </div>
  );
}

// Apple's mark, black on the white button. Per Apple's HIG it may only be
// rendered in black or white and must not be restyled.
function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#000"
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-8H6.5C9.8 35.6 16.4 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.3l6.2 5.2C41.4 35.2 44 30 44 24c0-1.3-.1-2.6-.4-3.9z"/>
    </svg>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 400, display: 'flex', alignItems: 'flex-end' },
  sheet: { background: '#141416', borderRadius: '28px 28px 0 0', padding: '14px 24px 40px', width: '100%', maxWidth: '430px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  handle: { width: '38px', height: '4px', background: '#333', borderRadius: '2px', margin: '0 auto 28px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#fff', margin: '0 0 24px' },
  warn: { display: 'flex', gap: '12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '14px', padding: '14px', marginBottom: '20px' },
  warnIcon: { fontSize: '18px', flexShrink: 0, lineHeight: 1.3 },
  warnTitle: { fontSize: '14px', fontWeight: '700', color: '#fbbf24', margin: '0 0 4px' },
  warnText: { fontSize: '12.5px', color: '#bbb', margin: '0 0 10px', lineHeight: 1.5 },
  warnBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '10px', padding: '8px 14px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  appleBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', background: '#fff', color: '#000', border: 'none', borderRadius: '14px', padding: '16px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '12px' },
  googleBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', background: '#fff', color: '#000', border: 'none', borderRadius: '14px', padding: '16px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '12px' },
  guestBtn: { width: '100%', background: 'rgba(255,255,255,0.06)', color: '#ccc', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '15px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
};
